import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { Link } from "react-router-dom";
import AxiosUrl from "../AxiosUrl";
import { toast } from "react-toastify";
import "./TicketDetailModal.css";
import moment from "moment";

function TicketDetailModal({ open, onClose, ticket, uid, onUpdate }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(ticket?.status || "pending");
  const [updating, setUpdating] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [investigating, setInvestigating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (ticket) {
      setMessages(ticket.messages || []);
      setStatus(ticket.status);
    }
  }, [ticket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await AxiosUrl.post(`/reply-to-ticket/${uid}`, {
        ticketId: ticket._id,
        message: newMessage.trim(),
      });

      if (response.data.success) {
        setMessages((prev) => [...prev, response.data.newMessage]);
        setNewMessage("");
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await AxiosUrl.post(`/update-report/${uid}`, {
        reportId: ticket._id,
        data: { status: newStatus },
      });

      if (response.data.success) {
        setStatus(newStatus);
        toast.success("Status updated successfully");
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.data.error || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleInvestigate = async () => {
    setInvestigating(true);
    try {
      const response = await AxiosUrl.post(`/ai-calling/ticket-agent/investigate/${ticket._id}/${uid}`, {});
      if (response.data.success) toast.success("Queued — the answer appears in the ticket (or as a draft on AI Calling → Ticket agent)");
      else toast.error(response.data.error || "Could not queue the ticket");
    } catch (error) {
      toast.error(error?.response?.status === 403 ? "You don't have access to AI calling" : "Could not queue the ticket");
    } finally {
      setInvestigating(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notifyMessage.trim()) {
      toast.error("Please enter a notification message");
      return;
    }

    setNotifying(true);
    try {
      const response = await AxiosUrl.post(`/notify-ticket-user/${uid}`, {
        ticketId: ticket._id,
        title: notifyTitle.trim() || "Support Ticket Update",
        message: notifyMessage.trim(),
      });

      if (response.data.success) {
        toast.success("Notification sent successfully");
        setNotifyOpen(false);
        setNotifyTitle("");
        setNotifyMessage("");
      } else {
        toast.error(response.data.error || "Failed to send notification");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setNotifying(false);
    }
  };

  const getStatusColor = (s) => {
    switch (s) {
      case "pending":
        return "#ff9800";
      case "in-progress":
        return "#2196f3";
      case "resolved":
        return "#4caf50";
      case "closed":
        return "#9e9e9e";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusLabel = (s) => {
    switch (s) {
      case "pending":
        return "Pending";
      case "in-progress":
        return "In Progress";
      case "resolved":
        return "Resolved";
      case "closed":
        return "Closed";
      default:
        return s;
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return moment(date).utcOffset('+06:00').format("MMM D, YYYY h:mm A");
  };

  if (!ticket) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="ticket-detail-modal"
    >
      <DialogTitle className="ticket-detail-header">
        <div className="ticket-detail-title-row">
          <div className="ticket-detail-info">
            <span className="ticket-number">
              #{ticket._id?.substring(0, 8).toUpperCase()}
            </span>
            <Chip
              label={getStatusLabel(status)}
              size="small"
              style={{
                backgroundColor: getStatusColor(status),
                color: "#fff",
                marginLeft: 12,
              }}
            />
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <div className="ticket-detail-meta">
          <span className="ticket-creator">
            <PersonIcon fontSize="small" />
            {ticket.createdBy?.username || "Guest"} - {ticket.contactPhone || "N/A"}
          </span>
          <span className="ticket-date">{formatDate(ticket.createdAt)}</span>
        </div>
      </DialogTitle>

      <DialogContent className="ticket-detail-content">
        {/* AI answered this ticket and handed it to the team (CornJob/Support/AiTicketResponder.js) */}
        {ticket.ai?.needsTeam && (
          <div style={{ background: "#fff4e5", border: "1px solid #ffb74d", borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 14 }}>
            <b>AI: needs the team</b>
            {ticket.ai.teamNote ? ` — ${ticket.ai.teamNote}` : ""}
          </div>
        )}
        {/* Ticket agent (AI Calling → Ticket agent): Claude Code investigates it read-only */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12, fontSize: 13 }}>
          {ticket.ai?.agentRunId && (
            <Link to={`/ai-calling/ticket-agent?run=${ticket.ai.agentRunId}`}>Ticket agent's investigation</Link>
          )}
          <Button size="small" variant="outlined" onClick={handleInvestigate} disabled={investigating}
            startIcon={investigating ? <CircularProgress size={14} /> : <ManageSearchIcon fontSize="small" />}>
            Investigate with the agent
          </Button>
        </div>
        {/* Initial Ticket Message */}
        <div className="initial-ticket">
          <div className="initial-ticket-header">
            <span className="initial-ticket-label">Initial Request</span>
            <span className="initial-ticket-type">
              {ticket.ticketType?.charAt(0).toUpperCase() +
                ticket.ticketType?.slice(1)}
            </span>
          </div>
          <p className="initial-ticket-description">{ticket.description}</p>
        </div>

        {/* Messages */}
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages yet. Send a reply to start the conversation.</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`message ${
                  msg.sender === "admin" ? "admin-message" : "user-message"
                }`}
              >
                <div className="message-header">
                  <span className="message-sender">
                    {msg.sender === "admin" ? (
                      <>
                        <SupportAgentIcon fontSize="small" />
                        {msg.senderName || "Admin"}
                      </>
                    ) : (
                      <>
                        <PersonIcon fontSize="small" />
                        {msg.senderName || "User"}
                      </>
                    )}
                  </span>
                  <span className="message-time">{formatDate(msg.createdAt)}</span>
                </div>
                <p className="message-content">{msg.content}</p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </DialogContent>

      <DialogActions className="ticket-detail-actions">
        <div className="actions-left">
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            size="small"
            variant="outlined"
            disabled={updating}
            style={{ minWidth: 140 }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<NotificationsActiveIcon />}
            onClick={() => setNotifyOpen(true)}
            style={{ marginLeft: 12 }}
          >
            Notify User
          </Button>
        </div>

        <div className="actions-right">
          {status !== "closed" && (
            <div className="reply-input-container">
              <TextField
                placeholder="Type your reply..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                size="small"
                variant="outlined"
                multiline
                maxRows={3}
                className="reply-input"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                endIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                className="send-button"
              >
                Send
              </Button>
            </div>
          )}
        </div>
      </DialogActions>

      {/* Send Notification Dialog */}
      <Dialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send Push Notification
          <IconButton
            onClick={() => setNotifyOpen(false)}
            size="small"
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Title (Optional)"
            value={notifyTitle}
            onChange={(e) => setNotifyTitle(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Support Ticket Update"
            style={{ marginTop: 8, marginBottom: 16 }}
          />
          <TextField
            label="Message"
            value={notifyMessage}
            onChange={(e) => setNotifyMessage(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            multiline
            rows={3}
            placeholder="Enter notification message..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendNotification}
            disabled={!notifyMessage.trim() || notifying}
            startIcon={
              notifying ? (
                <CircularProgress size={16} />
              ) : (
                <NotificationsActiveIcon />
              )
            }
          >
            {notifying ? "Sending..." : "Send Notification"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default TicketDetailModal;
